// src/components/ComplianceHub.js
import React, { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';

// Helper component for permission denied message
const PermissionDenied = () => (
    <div className="p-6 text-center">
        <div className="bg-white shadow-lg rounded-lg max-w-md mx-auto p-8">
             <svg className="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h2 className="mt-4 text-2xl font-bold text-red-600">Access Denied</h2>
            <p className="mt-2 text-gray-600">You do not have the necessary permissions to view this page.</p>
        </div>
    </div>
);


const ComplianceHub = ({ authAxios, users, clients, showFormMessage, fetchComplianceItems, fetchClients, userRole }) => {
    const [complianceItems, setComplianceItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingItem, setEditingItem] = useState(null);

    // --- RBAC Permissions ---
    const permissions = useMemo(() => ({
        canViewPage: ['PARTNER', 'ASSOCIATE', 'PARALEGAL', 'SUPERUSER'].includes(userRole),
        canCreate: userRole === 'PARTNER' || userRole === 'SUPERUSER',
        canEditFull: userRole === 'PARTNER' || userRole === 'SUPERUSER',
        canEditStatus: ['PARTNER', 'ASSOCIATE', 'PARALEGAL', 'SUPERUSER'].includes(userRole),
    }), [userRole]);


    // --- State for search, sort, and filter ---
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [regulationFilter, setRegulationFilter] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'due_date', direction: 'ascending' });

    const regulationOptions = [
        { value: 'FIA', label: 'Financial Intelligence Act' },
        { value: 'DPA', label: 'Data Protection Act' },
        { value: 'OTHER', label: 'Other' },
    ];

    const statusOptions = [
        { value: 'IN_PROGRESS', label: 'In Progress' },
        { value: 'COMPLIANT', label: 'Compliant' },
        { value: 'NON_COMPLIANT', label: 'Non-Compliant' },
        { value: 'NOT_APPLICABLE', label: 'Not Applicable' },
    ];

    const localFetchComplianceItems = async () => {
        setLoading(true);
        try {
            const response = await authAxios.get('compliance-items/');
            setComplianceItems(response.data.results || []);
        } catch (error) {
            console.error("Failed to fetch compliance items:", error);
            showFormMessage('danger', 'Could not load compliance items.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (permissions.canViewPage) {
            localFetchComplianceItems();
        } else {
            setLoading(false);
        }
    }, [permissions.canViewPage, authAxios]);

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        const isEditing = editingItem && editingItem.id;

        if (isEditing && !permissions.canEditStatus) {
            showFormMessage('danger', 'You do not have permission to edit this item.');
            return;
        }
        if (!isEditing && !permissions.canCreate) {
             showFormMessage('danger', 'You do not have permission to create a new item.');
            return;
        }

        const url = isEditing ? `compliance-items/${editingItem.id}/` : 'compliance-items/';
        const method = isEditing ? 'patch' : 'post';

        const payload = { ...editingItem };
        if (payload.due_date === '') payload.due_date = null;
        if (payload.completed_date === '') payload.completed_date = null;

        try {
            await authAxios[method](url, payload);
            showFormMessage('success', `Compliance item ${isEditing ? 'updated' : 'created'} successfully!`);
            setEditingItem(null);
            localFetchComplianceItems();
            if (fetchClients) fetchClients();
        } catch (error) {
            console.error("Failed to save compliance item:", error.response?.data);
            showFormMessage('danger', 'Failed to save compliance item.');
        }
    };
    
    const openNewItemForm = () => {
        if (!permissions.canCreate) {
            showFormMessage('warning', 'You do not have permission to add new compliance items.');
            return;
        }
        setEditingItem({
            client: '',
            requirement: '',
            regulation: 'FIA',
            status: 'IN_PROGRESS',
            assigned_to: '',
            due_date: '',
            completed_date: '',
            notes: ''
        });
    };
    
    const handleEditClick = (item) => {
        if (!permissions.canEditStatus) {
            showFormMessage('warning', 'You do not have permission to edit compliance items.');
            return;
        }
        setEditingItem(item);
    };

    const filteredAndSortedItems = useMemo(() => {
        let items = [...complianceItems];

        if (searchTerm) {
            items = items.filter(item =>
                item.requirement.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (item.client_name && item.client_name.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }
        if (statusFilter) {
            items = items.filter(item => item.status === statusFilter);
        }
        if (regulationFilter) {
            items = items.filter(item => item.regulation === regulationFilter);
        }

        items.sort((a, b) => {
            if (!a[sortConfig.key] || a[sortConfig.key] < b[sortConfig.key]) {
                return sortConfig.direction === 'ascending' ? -1 : 1;
            }
            if (!b[sortConfig.key] || a[sortConfig.key] > b[sortConfig.key]) {
                return sortConfig.direction === 'ascending' ? 1 : -1;
            }
            return 0;
        });

        return items;
    }, [complianceItems, searchTerm, statusFilter, regulationFilter, sortConfig]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };
    
    const getSortIndicator = (key) => {
        if (sortConfig.key === key) {
            return sortConfig.direction === 'ascending' ? '▲' : '▼';
        }
        return '';
    };

    if (!permissions.canViewPage) {
        return <PermissionDenied />;
    }
    
    const getStatusBadge = (status) => {
        switch (status) {
            case 'COMPLIANT': return 'bg-green-100 text-green-800';
            case 'NON_COMPLIANT': return 'bg-red-100 text-red-800';
            case 'IN_PROGRESS': return 'bg-yellow-100 text-yellow-800';
            case 'NOT_APPLICABLE': return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <div className="p-4 sm:p-6 animate__animated animate__fadeIn">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2 sm:mb-0">Regulatory Compliance Hub</h1>
                {permissions.canCreate && (
                    <button 
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        onClick={openNewItemForm}
                    >
                       <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                           <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                       </svg>
                        Add Compliance Item
                    </button>
                )}
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <input
                        type="text"
                        className="md:col-span-2 w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="Search by requirement or client..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <select className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        <option value="">All Statuses</option>
                        {statusOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                    <select className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" value={regulationFilter} onChange={(e) => setRegulationFilter(e.target.value)}>
                        <option value="">All Regulations</option>
                        {regulationOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                </div>
            </div>
            
            {loading ? <div className="text-center p-8"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto"></div></div> : (
                <div className="overflow-x-auto bg-white rounded-lg shadow">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th onClick={() => requestSort('requirement')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Requirement {getSortIndicator('requirement')}</th>
                                <th onClick={() => requestSort('client_name')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Client {getSortIndicator('client_name')}</th>
                                <th onClick={() => requestSort('regulation_display')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Regulation {getSortIndicator('regulation_display')}</th>
                                <th onClick={() => requestSort('status_display')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Status {getSortIndicator('status_display')}</th>
                                <th onClick={() => requestSort('due_date')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Due Date {getSortIndicator('due_date')}</th>
                                <th onClick={() => requestSort('assigned_to_username')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Assigned To {getSortIndicator('assigned_to_username')}</th>
                                {permissions.canEditStatus && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredAndSortedItems.map(item => (
                                <tr key={item.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.requirement}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.client_name}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.regulation_display}</td>
                                    <td className="px-6 py-4 whitespace-nowrap"><span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(item.status)}`}>{item.status_display}</span></td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.due_date ? format(new Date(item.due_date), 'yyyy-MM-dd') : 'N/A'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.assigned_to_username || 'Unassigned'}</td>
                                    {permissions.canEditStatus && (
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <button className="text-indigo-600 hover:text-indigo-900" onClick={() => handleEditClick(item)}>
                                                Edit
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {editingItem && (
                 <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
                         <div className="flex justify-between items-center mb-4">
                            <h5 className="text-xl font-bold text-gray-800">{editingItem.id ? 'Edit' : 'Create'} Compliance Item</h5>
                            <button onClick={() => setEditingItem(null)} className="text-gray-600 hover:text-gray-900">
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                         <form onSubmit={handleFormSubmit}>
                            <fieldset disabled={!permissions.canEditStatus} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Client</label>
                                        <select className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" value={editingItem.client || ''} onChange={e => setEditingItem({...editingItem, client: e.target.value})} required disabled={!permissions.canEditFull}>
                                            <option value="">Select Client</option>
                                            {clients.map(c => <option key={c.id} value={c.id}>{c.first_name ? `${c.first_name} ${c.last_name}` : c.organization_name}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Requirement</label>
                                        <input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" value={editingItem.requirement || ''} onChange={e => setEditingItem({...editingItem, requirement: e.target.value})} required disabled={!permissions.canEditFull} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Regulation</label>
                                         <select className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" value={editingItem.regulation || 'FIA'} onChange={e => setEditingItem({...editingItem, regulation: e.target.value})} disabled={!permissions.canEditFull}>
                                            {regulationOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Status</label>
                                         <select className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" value={editingItem.status || 'IN_PROGRESS'} onChange={e => setEditingItem({...editingItem, status: e.target.value})}>
                                            {statusOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Assigned To</label>
                                         <select className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" value={editingItem.assigned_to || ''} onChange={e => setEditingItem({...editingItem, assigned_to: e.target.value})} disabled={!permissions.canEditFull}>
                                            <option value="">Unassigned</option>
                                            {users.map(u => <option key={u.id} value={u.id}>{u.username}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Due Date</label>
                                        <input type="date" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" value={editingItem.due_date || ''} onChange={e => setEditingItem({...editingItem, due_date: e.target.value})} disabled={!permissions.canEditFull} />
                                    </div>
                                     <div>
                                        <label className="block text-sm font-medium text-gray-700">Completed Date</label>
                                        <input type="date" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" value={editingItem.completed_date || ''} onChange={e => setEditingItem({...editingItem, completed_date: e.target.value})} disabled={!permissions.canEditFull} />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700">Notes</label>
                                        <textarea className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" rows="3" value={editingItem.notes || ''} onChange={e => setEditingItem({...editingItem, notes: e.target.value})}></textarea>
                                    </div>
                                </div>
                                <div className="pt-4 flex justify-end space-x-2">
                                    <button type="button" className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300" onClick={() => setEditingItem(null)}>Cancel</button>
                                    <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Save</button>
                                </div>
                            </fieldset>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ComplianceHub;
